import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Brand } from "./Brand";
import {
  LayoutDashboard, Target, History, Award, LogOut, Menu, X,
  Users, CalendarPlus, Building2, Briefcase, ClipboardList, ShieldCheck, Sliders, FileText, CreditCard, GitBranch,
  BarChart3, Settings, ListTodo,
} from "lucide-react";

const NAV = {
  candidate: [
    { to: "/candidate", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/candidate/book", label: "Start Interview", icon: Target },
    { to: "/candidate/readiness", label: "Readiness Profile", icon: Award },
    { to: "/candidate/history", label: "Interview History", icon: History },
    { to: "/candidate/billing", label: "Billing & Plan", icon: CreditCard },
  ],
  agency_admin: [
    { to: "/agency", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/agency/clients", label: "Clients", icon: Building2 },
    { to: "/agency/candidates", label: "Candidates", icon: Users },
    { to: "/agency/interviews", label: "Interviews", icon: CalendarPlus },
    { to: "/agency/pipeline", label: "Pipeline", icon: GitBranch },
    { to: "/agency/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/agency/settings", label: "Team & Settings", icon: Settings },
    { to: "/agency/billing", label: "Billing & Credits", icon: CreditCard },
  ],
  recruitment_manager: [
    { to: "/agency", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/agency/work-queue", label: "Work Queue", icon: ListTodo },
    { to: "/agency/clients", label: "Clients", icon: Building2 },
    { to: "/agency/candidates", label: "Candidates", icon: Users },
    { to: "/agency/interviews", label: "Interviews", icon: CalendarPlus },
    { to: "/agency/pipeline", label: "Pipeline", icon: GitBranch },
    { to: "/agency/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/agency/settings", label: "Team & Settings", icon: Settings },
  ],
  recruiter: [
    { to: "/agency", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/agency/work-queue", label: "My Work", icon: ListTodo },
    { to: "/agency/clients", label: "Clients", icon: Building2 },
    { to: "/agency/candidates", label: "Candidates", icon: Users },
    { to: "/agency/interviews", label: "Interviews", icon: CalendarPlus },
    { to: "/agency/pipeline", label: "Pipeline", icon: GitBranch },
    { to: "/agency/analytics", label: "Analytics", icon: BarChart3 },
  ],
  college_admin: [
    { to: "/college", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/college/students", label: "Students", icon: Users },
    { to: "/college/sponsor", label: "Sponsor Interview", icon: CalendarPlus },
    { to: "/college/reports", label: "Reports", icon: FileText },
    { to: "/college/billing", label: "Billing & Credits", icon: CreditCard },
  ],
  interviewer: [
    { to: "/expert", label: "Assigned Interviews", icon: ClipboardList, end: true },
  ],
  platform_admin: [
    { to: "/admin", label: "Platform Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/agencies", label: "Agencies", icon: Briefcase },
    { to: "/admin/config", label: "Readiness Config", icon: Sliders },
    { to: "/admin/billing", label: "Billing & Plans", icon: CreditCard },
  ],
};

const ROLE_LABEL = {
  candidate: "Candidate", college_admin: "College Admin",
  interviewer: "Interview Expert", platform_admin: "Platform Admin",
  agency_admin: "Agency Admin", recruiter: "Recruiter", recruitment_manager: "Recruitment Manager",
};

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [branding, setBranding] = useState(null);
  const items = NAV[user.role] || [];

  useEffect(() => {
    if (user.role === "college_admin") {
      api.get("/college/branding").then((r) => setBranding(r.data)).catch(() => {});
    }
  }, [user.role]);

  const CollegeBrand = () => (
    <div data-testid="college-sidebar-brand" className="flex items-center gap-3">
      {branding?.logo ? (
        <img src={branding.logo} alt="College logo" data-testid="college-sidebar-logo" className="h-10 w-10 object-contain rounded-lg bg-slate-50 border border-slate-200 p-1 shrink-0" />
      ) : (
        <span className="h-10 w-10 rounded-lg bg-brand-light grid place-items-center shrink-0"><Building2 className="h-5 w-5 text-brand" /></span>
      )}
      <div className="min-w-0">
        <div className="font-heading font-extrabold text-ink text-sm leading-tight truncate">{branding?.college_name || user.college_name || "College"}</div>
        <div className="text-[10px] text-ink-faint font-semibold mt-0.5">powered by Mock<span className="text-brand">2</span>Crack</div>
      </div>
    </div>
  );

  const isAgency = ["agency_admin", "recruiter", "recruitment_manager"].includes(user.role);

  const AgencyBrand = () => (
    <div data-testid="agency-sidebar-brand" className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-lg bg-brand-light grid place-items-center shrink-0"><Briefcase className="h-5 w-5 text-brand" /></span>
      <div className="min-w-0">
        <div className="font-heading font-extrabold text-ink text-sm leading-tight truncate">{user.agency_name || "Recruitment Agency"}</div>
        <div className="text-[10px] text-ink-faint font-semibold mt-0.5">powered by Mock<span className="text-brand">2</span>Crack</div>
      </div>
    </div>
  );

  const isActive = (it) => it.end ? location.pathname === it.to : location.pathname.startsWith(it.to);

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-slate-200">
        {user.role === "college_admin" ? <CollegeBrand /> : isAgency ? <AgencyBrand /> : <Brand to="/" />}
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        {items.map((it) => {
          const Icon = it.icon;
          const active = isActive(it);
          return (
            <Link
              key={it.to} to={it.to} data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                active ? "bg-brand text-white shadow-ambient" : "text-ink-soft hover:bg-slate-100"
              }`}
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
              {it.label}
            </Link>
          );
        })}
      </nav>
      <div className="px-3 pb-5">
        <div className="px-3.5 py-3 rounded-xl bg-slate-50 border border-slate-200 mb-2">
          <div className="text-sm font-bold text-ink truncate">{user.name}</div>
          <div className="text-xs text-ink-faint">{ROLE_LABEL[user.role]}</div>
          {user.college_name && <div className="text-[11px] text-brand font-semibold mt-0.5 truncate">{user.college_name}</div>}
          {user.agency_name && <div className="text-[11px] text-brand font-semibold mt-0.5 truncate">{user.agency_name}</div>}
        </div>
        <button
          data-testid="logout-button"
          onClick={() => { logout(); navigate("/login"); }}
          className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-xl text-sm font-semibold text-ink-soft hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" /> Log out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-slate-200 bg-white flex-col fixed inset-y-0 z-30">
        <SideContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-lift">
            <SideContent />
          </div>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 glass border-b border-slate-200 px-4 h-16 flex items-center justify-between">
          <Brand to="/" />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-slate-100">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </header>
        <main className="p-5 sm:p-8 max-w-[1400px] mx-auto fade-up">{children}</main>
      </div>
    </div>
  );
}
